import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  restoreAdminUser,
  getPeriods,
  createPeriod,
  updatePeriod,
  deletePeriod,
  getEmployeeTypes,
  createEmployeeType,
  updateEmployeeType,
  deleteEmployeeType,
  getAppSettings,
  updateAppSettings,
  type CreateUserInput,
  type UpdateUserInput,
  type DeleteUserOptions,
  type CreatePeriodInput,
  type AppSettings,
  type ClaudeTierChoice,
} from '../services/api/admin';

// ===== User Hooks =====
/**
 * `enabled` (Phase 129, D-05) lets a caller that may not be an admin skip the request entirely:
 * the project edit page's member-only Engineering view passes `false`. Every other caller keeps
 * today's behaviour through the default.
 */
export function useAdminUsers(includeDeleted = false, enabled = true) {
  return useQuery({
    queryKey: ['adminUsers', includeDeleted],
    queryFn: () => getAdminUsers(includeDeleted),
    enabled,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => createAdminUser(input),
    onSuccess: (data) => {
      // F7-001: createUser immediately enqueues an onboarding workflow run
      // (userAdminService.createUser → scheduleWorkflowRun). The admin's
      // useUserWorkflowRuns polling is gated on an already-loaded
      // non-terminal run, so without an invalidation the fresh trigger
      // never lights up the polling interval. Mirror the F6-001 fix that
      // landed on useDeleteUser / useRestoreUser.
      const keys: QueryKey[] = [['adminUsers'], ['userWorkflowRuns']];
      if (data && typeof data.id === 'number') {
        keys.push(['userWorkflowRuns', data.id]);
      }
      invalidateMany(queryClient, keys);
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateUserInput }) => updateAdminUser(id, input),
    onSuccess: (_data, { id }) => {
      // F7-006: updateUser can flip roles[], employeeType, slackUserId,
      // phoneNumber — all of which feed downstream caches that
      // previously stayed stale until manual refresh. Invalidate the
      // user-scoped surfaces (admin detail + workflow runs) and the
      // role-derived approval cockpit so a promote-to-manager /
      // grant-finance-role takes effect without a page refresh.
      // Roles change may re-trigger or alter onboarding access — keep
      // the F6-001 / F7-001 useUserWorkflowRuns surface in sync.
      invalidateMany(queryClient, [
        ['adminUsers'],
        ['adminUser', id],
        ['users'],
        ['admin-users'],
        ['approvalCounts'],
        ['userWorkflowRuns', id],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, options }: { id: number; options?: DeleteUserOptions }) => deleteAdminUser(id, options),
    onSuccess: (_data, { id }) => {
      // F6-001: surfaced offboarding workflow run via useUserWorkflowRuns —
      // polling is gated on a non-terminal run being already loaded, so a
      // fresh trigger never lights up the interval without an invalidation.
      invalidateMany(queryClient, [
        ['adminUsers'],
        ['userWorkflowRuns', id],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useRestoreUser() {
  const queryClient = useQueryClient();
  return useMutation({
    // FBV-01: accept {id, claudeTier} so callers can pass the admin's tier
    // choice. claudeTier is forwarded to restoreAdminUser which sends it in
    // the POST body to the backend.
    mutationFn: ({ id, claudeTier, skipOnboarding }: { id: number; claudeTier?: ClaudeTierChoice; skipOnboarding?: boolean }) =>
      restoreAdminUser(id, claudeTier, skipOnboarding),
    onSuccess: (_data, { id }) => {
      // F6-001: surfaced onboarding workflow run via useUserWorkflowRuns —
      // polling is gated on a non-terminal run being already loaded, so a
      // fresh trigger never lights up the interval without an invalidation.
      invalidateMany(queryClient, [
        ['adminUsers'],
        ['userWorkflowRuns', id],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

// ===== Period Hooks =====
export function usePeriods() {
  return useQuery({
    queryKey: ['periods'],
    queryFn: getPeriods,
  });
}

export function useCreatePeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePeriodInput) => createPeriod(input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['periods'] }); },
  });
}

export function useUpdatePeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<CreatePeriodInput> }) => updatePeriod(id, input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['periods'] }); },
  });
}

export function useDeletePeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePeriod(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['periods'] }); },
  });
}

// ===== Employee Type Hooks =====
export function useEmployeeTypes() {
  return useQuery({
    queryKey: ['employeeTypes'],
    queryFn: getEmployeeTypes,
  });
}

export function useCreateEmployeeType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; totalHoursPerMonth?: number }) => createEmployeeType(input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['employeeTypes'] }); },
  });
}

export function useUpdateEmployeeType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: { name?: string; totalHoursPerMonth?: number | null } }) =>
      updateEmployeeType(id, input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['employeeTypes'] }); },
  });
}

export function useDeleteEmployeeType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEmployeeType(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['employeeTypes'] }); },
  });
}

// ===== App Settings Hooks =====
export function useAppSettings() {
  return useQuery({
    queryKey: ['appSettings'],
    queryFn: getAppSettings,
  });
}

export function useUpdateAppSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<AppSettings>) => updateAppSettings(input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['appSettings'] }); },
  });
}
